import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, FileUp, Info } from 'lucide-react';
import type { PromotionPolicyImportProductMatch } from '@sleed/bizcommerce-api';
import { Button } from '@/components/ui/button';
import { EditorSection, Field } from '~/components/promotion/editor-section';
import { EntityMultiSelect } from '~/components/promotion/entity-multi-select';
import { BulkImportProductsDialog } from '~/components/promotion/conditions/bulk-import-products-dialog';
import { SegmentedControl } from '~/components/segmented-control';
import { toGreekCaps } from '~/lib/i18n/toGreekCaps';
import type { LookupOption } from '~/lib/lookups/mock';
import { MOCK_CATEGORIES, MOCK_MANUFACTURERS, loadMockProducts } from '~/lib/lookups/mock';
import type { PromotionFormValues } from '~/lib/schemas/promotion';

/** Maps a bulk-import match to a picker option so imported products show a real label (code · name). */
function matchToOption(match: PromotionPolicyImportProductMatch): LookupOption {
	return { id: match.id ?? 0, label: match.name || match.code || `#${match.id}`, sublabel: match.code ?? undefined };
}

/**
 * Constraint #1 — TWO separate combinator fields:
 *  - INNER `productMatchCondition` (any/all → ConditionCombinationMode.Or/And):
 *    quantifier over the selected specific products (≥1 / all in cart).
 *  - OUTER `productSelectionConditionMode` (or/and): boolean across the Products /
 *    Categories / Manufacturers blocks.
 * Rendered as visibly distinct controls (inner inline with the products label,
 * outer in a dedicated divider) with different defaults (inner=any, outer=and).
 */
export function ProductSelectionSection() {
	const { t } = useTranslation();
	const { control, getValues, setValue } = useFormContext<PromotionFormValues>();
	const [bulkImportOpen, setBulkImportOpen] = useState(false);
	// Options for products brought in via bulk import, so they render with a real label despite the
	// products picker's placeholder (mock) lookup.
	const [importedOptions, setImportedOptions] = useState<LookupOption[]>([]);

	const handleImported = (matched: PromotionPolicyImportProductMatch[]) => {
		// Keep productIds and importedOptions consistent: only matches with a real numeric id count.
		const withIds = matched.filter((m): m is PromotionPolicyImportProductMatch & { id: number } => typeof m.id === 'number');
		const merged = Array.from(new Set([...getValues('productIds'), ...withIds.map(m => m.id)]));
		setValue('productIds', merged, { shouldDirty: true });

		setImportedOptions(prev => {
			const byId = new Map(prev.map(o => [o.id, o]));
			for (const match of withIds) byId.set(match.id, matchToOption(match));
			return [...byId.values()];
		});
	};

	return (
		<EditorSection
			title={t('promotion.conditions.productSelection.title')}
			subtitle={t('promotion.conditions.productSelection.subtitle')}
			icon={Box}>
			<div className="flex items-start gap-2.5 rounded-md border border-info-border bg-info p-3">
				<Info size={16} className="mt-0.5 shrink-0 text-info-fg" />
				<p className="text-xs text-info-fg">{t('promotion.conditions.productSelection.banner')}</p>
			</div>

			<div className="mt-4">
				<div className="mb-1.5 flex items-center justify-between gap-3">
					<span className="text-sm font-medium text-foreground">{t('promotion.conditions.productSelection.products')}</span>
					<div className="flex items-center gap-2">
						<Button variant="outline" size="sm" type="button" onClick={() => setBulkImportOpen(true)}>
							<FileUp size={14} /> {t('promotion.conditions.productSelection.bulkImport.trigger')}
						</Button>
						<Controller
							control={control}
							name="productMatchCondition"
							render={({ field }) => (
								<SegmentedControl
									size="sm"
									value={field.value}
									onValueChange={field.onChange}
									options={[
										{ value: 'any', symbol: '∨', label: t('promotion.conditions.combinators.inner.any') },
										{ value: 'all', symbol: '∧', label: t('promotion.conditions.combinators.inner.all') }
									]}
								/>
							)}
						/>
					</div>
				</div>
				<Controller
					control={control}
					name="productIds"
					render={({ field }) => (
						<EntityMultiSelect
							value={field.value}
							onChange={field.onChange}
							options={importedOptions}
							loadOptions={loadMockProducts}
							placeholder={t('promotion.conditions.productSelection.productsPlaceholder')}
						/>
					)}
				/>
				<p className="mt-1.5 text-xs text-foreground-muted">{t('promotion.conditions.productSelection.productsHelp')}</p>
			</div>

			<div className="my-5 flex items-center gap-3">
				<div className="h-px flex-1 bg-border" />
				<div className="flex flex-col items-center gap-1.5">
					<span className="text-[11px] font-semibold tracking-[0.04em] text-foreground-muted">
						{toGreekCaps(t('promotion.conditions.combinators.outer.label'))}
					</span>
					<Controller
						control={control}
						name="productSelectionConditionMode"
						render={({ field }) => (
							<SegmentedControl
								size="sm"
								value={field.value}
								onValueChange={field.onChange}
								options={[
									{ value: 'or', symbol: '∨', label: t('promotion.conditions.combinators.outer.or') },
									{ value: 'and', symbol: '∧', label: t('promotion.conditions.combinators.outer.and') }
								]}
							/>
						)}
					/>
				</div>
				<div className="h-px flex-1 bg-border" />
			</div>

			<div className="grid grid-cols-2 gap-4">
				<Field
					label={t('promotion.conditions.productSelection.categories')}
					help={t('promotion.conditions.productSelection.categoriesHelp')}>
					<Controller
						control={control}
						name="categoryIds"
						render={({ field }) => (
							<EntityMultiSelect
								value={field.value}
								onChange={field.onChange}
								options={MOCK_CATEGORIES}
								placeholder={t('promotion.conditions.productSelection.categoriesPlaceholder')}
							/>
						)}
					/>
				</Field>
				<Field
					label={t('promotion.conditions.productSelection.manufacturers')}
					help={t('promotion.conditions.productSelection.manufacturersHelp')}>
					<Controller
						control={control}
						name="manufacturerIds"
						render={({ field }) => (
							<EntityMultiSelect
								value={field.value}
								onChange={field.onChange}
								options={MOCK_MANUFACTURERS}
								placeholder={t('promotion.conditions.productSelection.manufacturersPlaceholder')}
							/>
						)}
					/>
				</Field>
			</div>

			<BulkImportProductsDialog open={bulkImportOpen} onOpenChange={setBulkImportOpen} onImported={handleImported} />
		</EditorSection>
	);
}
